import React, { useState, useEffect, useMemo } from "react";
import { X, Barcode, CheckCircle, Keyboard } from "@phosphor-icons/react";
import useHIDScanner from "../lib/useHIDScanner"; // Make sure path matches your hooks folder
import { api } from "../lib/api";

export default function HT20ScannerModal({ onClose, onDetected, currentScannedBarcodes = [] }) {
  const [scannedList, setScannedList] = useState([]);
  const [manual, setManual] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  // In-stock bag catalogue for autocomplete — fetched once when modal opens.
  const [catalogue, setCatalogue] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.get("/inventory", { params: { status: "in_stock" } })
      .then((r) => {
        if (!mounted) return;
        const rows = (r.data || [])
          .filter((x) => x.barcode && (x.remaining_weight || 0) > 0)
          .map((x) => ({
            barcode: x.barcode,
            raw_material_name: x.raw_material_name,
            remaining_weight: x.remaining_weight,
            unit: x.unit || "kg",
          }));
        setCatalogue(rows);
      })
      .catch(() => setCatalogue([]));
    return () => { mounted = false; };
  }, []);

  // Ranked, deduped suggestions: prefix matches on barcode first, then substring
  // matches on barcode / raw material name. Cap at 8 so the dropdown stays tidy.
  const suggestions = useMemo(() => {
    const q = manual.trim().toLowerCase();
    if (!q) return [];
    const taken = new Set([...(currentScannedBarcodes || []), ...scannedList]);
    const scored = [];
    for (const it of catalogue) {
      if (taken.has(it.barcode)) continue;
      const b = it.barcode.toLowerCase();
      const n = (it.raw_material_name || "").toLowerCase();
      let score = -1;
      if (b.startsWith(q)) score = 0;
      else if (b.includes(q)) score = 1;
      else if (n.includes(q)) score = 2;
      if (score >= 0) scored.push({ ...it, _score: score });
    }
    scored.sort((a, b) => a._score - b._score || a.barcode.localeCompare(b.barcode));
    return scored.slice(0, 8);
  }, [manual, catalogue, currentScannedBarcodes, scannedList]);

  // Shared entry pipeline: dedupe against current + this-session list, then hand
  // off to the parent's `onDetected` (same path the hardware scanner takes).
  const submitCode = (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    if (currentScannedBarcodes.includes(code) || scannedList.includes(code)) return;
    setScannedList((prev) => [code, ...prev]);
    onDetected(code);
  };

  const submitManual = (e) => {
    e?.preventDefault?.();
    // If suggestions are showing and one is highlighted (top result), prefer it
    // so operators can type a partial code and hit Enter without clicking.
    if (suggestions.length && manual.trim() && !catalogue.some(x => x.barcode === manual.trim())) {
      submitCode(suggestions[0].barcode);
    } else {
      if (!manual.trim()) return;
      submitCode(manual);
    }
    setManual("");
    setShowSuggest(false);
  };

  // Use your existing hook. Every time the HT20 trigger is pulled,
  // it fires this callback, updates our local list, and sends it to the parent.
  useHIDScanner({
    onScan: (code) => submitCode(code),
    enabled: true,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md border border-zinc-300 shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2">
            <Barcode size={22} className="text-orange-600 animate-pulse" />
            <div>
              <div className="font-display font-black text-sm uppercase tracking-wider">HT20 Scanner Active</div>
              <div className="text-[10px] text-zinc-500 font-mono">Ready to receive scans</div>
            </div>
          </div>
          <button 
            data-testid="scanner-close" 
            onClick={onClose} 
            className="text-zinc-500 hover:text-zinc-950 p-1 rounded hover:bg-zinc-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 text-center">
          
          {/* Waiting visual indicator */}
          <div className="my-4 py-6 border-2 border-dashed border-orange-200 bg-orange-50/30 rounded-lg">
            <p className="text-sm font-semibold text-zinc-800">Point your Helett HT20 & pull trigger</p>
            <p className="text-xs text-zinc-500 mt-1 font-mono">Input auto-focuses here</p>
          </div>

          {/* Manual entry fallback — when the scanner isn't reachable or the
              operator only has the printed label / a phone photo in hand.
              Autocomplete suggests matching in-stock bags as they type. */}
          <form onSubmit={submitManual} className="mt-4 text-left relative">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 font-mono flex items-center gap-1.5">
              <Keyboard size={13} weight="bold" /> Or type / paste barcode
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={manual}
                onChange={(e) => { setManual(e.target.value); setShowSuggest(true); }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                placeholder="e.g. SL2 or Salt — starts suggesting after 1 char"
                autoFocus
                autoComplete="off"
                className="flex-1 border border-zinc-300 rounded px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:border-orange-500"
                data-testid="scanner-manual-input"
              />
              <button
                type="submit"
                disabled={!manual.trim()}
                className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-wider rounded transition-colors"
                data-testid="scanner-manual-add"
              >
                Add
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-1 font-mono">
              Press Enter to add the top match. Duplicates auto-skip.
            </p>

            {/* Autocomplete dropdown */}
            {showSuggest && suggestions.length > 0 && (
              <div
                className="absolute left-0 right-0 top-[72px] bg-white border border-zinc-200 rounded shadow-lg z-10 max-h-56 overflow-y-auto"
                data-testid="scanner-suggestions"
              >
                {suggestions.map((s, i) => (
                  <button
                    key={s.barcode}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); submitCode(s.barcode); setManual(""); setShowSuggest(false); }}
                    className={`w-full text-left px-2.5 py-1.5 flex items-center gap-2 text-xs font-mono hover:bg-orange-50 border-b border-zinc-100 last:border-b-0 ${i === 0 ? "bg-orange-50/50" : ""}`}
                    data-testid={`scanner-suggest-${s.barcode}`}
                  >
                    <span className="font-bold text-orange-700">{s.barcode}</span>
                    <span className="text-zinc-600 truncate">{s.raw_material_name}</span>
                    <span className="ml-auto text-zinc-500 flex-shrink-0">{s.remaining_weight}{s.unit}</span>
                  </button>
                ))}
              </div>
            )}
          </form>

          {/* List of scanned items in this session */}
          <div className="text-left mt-6">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 font-mono">
              Scanned in this session ({scannedList.length})
            </div>
            
            {scannedList.length === 0 ? (
              <p className="text-xs italic text-zinc-400 font-mono py-2">No barcodes scanned yet...</p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1 border border-zinc-100 rounded p-2 bg-zinc-50">
                {scannedList.map((barcode, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between text-xs font-mono bg-white border border-green-200 px-2 py-1.5 rounded text-green-900 animate-fadeIn"
                  >
                    <span className="font-bold">{barcode}</span>
                    <span className="flex items-center gap-1 text-[10px] text-green-600 font-sans font-semibold">
                      <CheckCircle size={12} weight="fill" /> Registered
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="p-3 border-t border-zinc-200 bg-zinc-50 flex justify-end">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-white font-mono text-xs font-bold uppercase tracking-wider"
          >
            Done Scanning
          </button>
        </div>

      </div>
    </div>
  );
}